/**
 * Factory Form Drawer
 * Create/Edit form for factories with location and cities selection
 */

import { useEffect, useState } from 'react'
import { useTranslation, useForm, Controller, zodResolver, z } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  Factory as FactoryIcon,
  Save,
  X,
  MapPin,
  Search,
} from '@spcc/uikit'
import type { Factory, Location, FactoryStatus, City } from '../../domain/types'
import { FACTORY_STATUS, T_KEYS } from '../../domain/constants'

// ============================================================================
// Types
// ============================================================================

export interface FactoryFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  factory?: Factory | null
  locations: Location[]
  cities: City[]
  onCitySearch?: (query: string) => void
  citiesSearchResults?: City[]
  citiesSearchLoading?: boolean
  citiesHasMore?: boolean
  citiesLoadMoreLoading?: boolean
  onLoadMoreCities?: () => void
  onSubmit: (data: FactoryFormSubmitData) => void
  isLoading?: boolean
  isRTL?: boolean
}

export interface FactoryFormSubmitData {
  code: string
  sapPlantCode: string
  name: { en: string; ar: string }
  description?: { en: string; ar: string }
  locationId?: string
  cityIds?: string[]
  status: FactoryStatus
}

// ============================================================================
// Schema & Types
// ============================================================================

interface FactoryFormData {
  code: string
  sapPlantCode: string
  nameEn: string
  nameAr: string
  descriptionEn: string
  descriptionAr: string
  locationId: string
  cityIds: string[]
  status: 'active' | 'inactive'
}

const factoryFormSchema = z.object({
  code: z
    .string()
    .min(1, T_KEYS.FACTORIES.VALIDATION.CODE_REQUIRED)
    .max(50, 'Code must be 50 characters or less'),
  sapPlantCode: z.string().max(10).default(''),
  nameEn: z.string().min(1, T_KEYS.FACTORIES.VALIDATION.NAME_REQUIRED),
  nameAr: z.string().default(''),
  descriptionEn: z.string().default(''),
  descriptionAr: z.string().default(''),
  locationId: z.string().default(''),
  cityIds: z.array(z.string()).default([]),
  status: z.enum(['active', 'inactive']).default('active'),
})

// ============================================================================
// Utility Functions
// ============================================================================

function generateFactoryCode(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 4).toUpperCase()
  return `FAC-${timestamp}-${random}`
}

// Special value for "no location selected"
const NO_LOCATION_VALUE = '__none__'

function getDefaultValues(factory?: Factory | null): FactoryFormData {
  if (factory) {
    // Extract city IDs from factory.cities relation
    const cityIds = (factory.cities ?? [])
      .map(city => city.documentId || String(city.id))
      .filter(Boolean)

    return {
      code: factory.code,
      sapPlantCode: factory.sapPlantCode || '',
      nameEn: factory.name.en,
      nameAr: factory.name.ar || '',
      descriptionEn: factory.description?.en || '',
      descriptionAr: factory.description?.ar || '',
      locationId: factory.location?.documentId || NO_LOCATION_VALUE,
      cityIds,
      status: factory.status,
    }
  }
  return {
    code: generateFactoryCode(),
    sapPlantCode: '',
    nameEn: '',
    nameAr: '',
    descriptionEn: '',
    descriptionAr: '',
    locationId: NO_LOCATION_VALUE,
    cityIds: [],
    status: 'active',
  }
}

function formDataToSubmitData(data: FactoryFormData): FactoryFormSubmitData {
  // Convert special "none" value back to undefined
  const locationId = data.locationId === NO_LOCATION_VALUE ? undefined : data.locationId

  return {
    code: data.code.trim().toUpperCase(),
    sapPlantCode: data.sapPlantCode.trim(),
    name: {
      en: data.nameEn.trim(),
      ar: data.nameAr?.trim() || data.nameEn.trim(),
    },
    description: data.descriptionEn
      ? {
          en: data.descriptionEn.trim(),
          ar: data.descriptionAr?.trim() || data.descriptionEn.trim(),
        }
      : undefined,
    locationId,
    cityIds: data.cityIds.length > 0 ? data.cityIds : undefined,
    status: data.status,
  }
}

// ============================================================================
// Component
// ============================================================================

export function FactoryFormDrawer({
  open,
  onOpenChange,
  factory,
  locations,
  cities,
  onCitySearch,
  citiesSearchResults,
  citiesSearchLoading = false,
  citiesHasMore = false,
  citiesLoadMoreLoading = false,
  onLoadMoreCities,
  onSubmit,
  isLoading = false,
  isRTL = false,
}: FactoryFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = !!factory
  const [citySearch, setCitySearch] = useState('')

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FactoryFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(factoryFormSchema) as any,
    defaultValues: getDefaultValues(factory),
  })

  // Watch cityIds for the checkbox logic
  const selectedCityIds = watch('cityIds')

  // Determine the city list to display: search results when searching, full list otherwise
  const displayCities = citySearch.trim() ? (citiesSearchResults ?? []) : (cities ?? [])

  // Toggle city selection
  const toggleCity = (cityDocumentId: string) => {
    const current = selectedCityIds || []
    if (current.includes(cityDocumentId)) {
      setValue('cityIds', current.filter(id => id !== cityDocumentId))
    } else {
      setValue('cityIds', [...current, cityDocumentId])
    }
  }

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      reset(getDefaultValues(factory))
      setCitySearch('')
      onCitySearch?.('')
    }
  }, [open, factory?.documentId, reset, factory, onCitySearch])

  const onFormSubmit = (data: FactoryFormData) => {
    onSubmit(formDataToSubmitData(data))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = handleSubmit(onFormSubmit as any)

  const headerContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <FactoryIcon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">
          {isEditing ? t(T_KEYS.FACTORIES.EDIT_FACTORY) : t(T_KEYS.FACTORIES.ADD_FACTORY)}
        </h2>
        <p className="text-sm text-white/80">
          {isEditing
            ? t(T_KEYS.FACTORIES.EDIT_DESCRIPTION)
            : t(T_KEYS.FACTORIES.ADD_DESCRIPTION)}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className={cn('flex gap-3')}>
      <Button
        type="button"
        variant="outline"
        className="flex-1 h-12 rounded-xl"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        <X className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        className="flex-1 h-12 rounded-xl"
        disabled={isLoading}
        onClick={handleSave}
      >
        <Save className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {isLoading ? t(T_KEYS.FACTORIES.SAVING) : t('common.save')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
      footerContent={footerContent}
      maxWidth="sm:max-w-lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <form id="factory-form" onSubmit={handleSubmit(onFormSubmit as any)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t(T_KEYS.FACTORIES.BASIC_INFO)}
          </h3>

          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">{t(T_KEYS.FACTORIES.FORM.CODE)} *</Label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="code"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.FORM.CODE_PLACEHOLDER)}
                    className={cn('h-11 rounded-xl uppercase', errors.code && 'border-destructive')}
                    dir="ltr"
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.code && <p className="text-xs text-destructive">{t(errors.code.message || '')}</p>}
          </div>

          {/* SAP Plant Code */}
          <div className="space-y-2">
            <Label htmlFor="sapPlantCode">{t(T_KEYS.FACTORIES.FORM.SAP_PLANT_CODE)}</Label>
            <Controller
              name="sapPlantCode"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="sapPlantCode"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.FORM.SAP_PLANT_CODE_PLACEHOLDER)}
                    className={cn('h-11 rounded-xl', errors.sapPlantCode && 'border-destructive')}
                    dir="ltr"
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.sapPlantCode && <p className="text-xs text-destructive">{t(errors.sapPlantCode.message || '')}</p>}
          </div>

          {/* Name (English) */}
          <div className="space-y-2">
            <Label htmlFor="nameEn">{t(T_KEYS.FACTORIES.FORM.NAME_EN)} *</Label>
            <Controller
              name="nameEn"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="nameEn"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.FORM.NAME_EN_PLACEHOLDER)}
                    className={cn('h-11 rounded-xl', errors.nameEn && 'border-destructive')}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.nameEn && <p className="text-xs text-destructive">{t(errors.nameEn.message || '')}</p>}
          </div>

          {/* Name (Arabic) */}
          <div className="space-y-2">
            <Label htmlFor="nameAr">{t(T_KEYS.FACTORIES.FORM.NAME_AR)}</Label>
            <Controller
              name="nameAr"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="nameAr"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.FORM.NAME_AR_PLACEHOLDER)}
                    className="h-11 rounded-xl"
                    dir="rtl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>{t(T_KEYS.FACTORIES.FORM.STATUS)}</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder={t(T_KEYS.FACTORIES.SELECT_STATUS)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FACTORY_STATUS.ACTIVE}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        {t(T_KEYS.FACTORIES.STATUS.ACTIVE)}
                      </span>
                    </SelectItem>
                    <SelectItem value={FACTORY_STATUS.INACTIVE}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-400" />
                        {t(T_KEYS.FACTORIES.STATUS.INACTIVE)}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t(T_KEYS.FACTORIES.LOCATION_INFO)}
          </h3>

          <div className="space-y-2">
            <Label>{t(T_KEYS.FACTORIES.FORM.ADDRESS)}</Label>
            <Controller
              name="locationId"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder={t(T_KEYS.FACTORIES.SELECT_LOCATION)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {t(T_KEYS.FACTORIES.NO_LOCATION_OPTION)}
                    </SelectItem>
                    {locations.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {t('common.loading')}
                      </div>
                    ) : (
                      locations.map((location) => (
                        <SelectItem key={location.id} value={location.documentId}>
                          <div className="flex flex-col">
                            <span>{location.name} ({location.code})</span>
                            {location.addressLine1 && (
                              <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                                {location.addressLine1}
                                {location.city?.name && `, ${location.city.name}`}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t(T_KEYS.FACTORIES.LOCATION_HINT)}
            </p>
          </div>
        </div>

        {/* Cities Served */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {t(T_KEYS.FACTORIES.CITIES_SERVED)}
          </h3>

          <p className="text-xs text-muted-foreground">
            {t(T_KEYS.FACTORIES.CITIES_SERVED_HINT)}
          </p>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={citySearch}
              onChange={(e) => {
                setCitySearch(e.target.value)
                onCitySearch?.(e.target.value)
              }}
              placeholder={t(T_KEYS.FACTORIES.SEARCH_CITIES)}
              className="h-10 pl-9 rounded-xl"
            />
          </div>

          {/* Cities list */}
          <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700">
            {citiesSearchLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : displayCities.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {citySearch
                  ? t(T_KEYS.FACTORIES.NO_CITIES_MATCH)
                  : t(T_KEYS.FACTORIES.NO_CITIES_AVAILABLE)}
              </div>
            ) : (
              displayCities.map((city) => {
                const cityId = city.documentId || String(city.id)
                const isSelected = (selectedCityIds || []).includes(cityId)
                return (
                  <label
                    key={city.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0',
                      isSelected
                        ? 'bg-primary/5 dark:bg-primary/10'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleCity(cityId)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{city.name}</p>
                      <p className="text-xs text-muted-foreground">{city.code}</p>
                    </div>
                    {isSelected && (
                      <span className="text-xs text-primary font-medium">
                        {t(T_KEYS.FACTORIES.SELECTED)}
                      </span>
                    )}
                  </label>
                )
              })
            )}
          </div>

          {/* Load More button — only shown when not searching and more pages exist */}
          {!citySearch.trim() && citiesHasMore && (
            <button
              type="button"
              onClick={onLoadMoreCities}
              disabled={citiesLoadMoreLoading}
              className={cn(
                'w-full py-2 text-sm font-medium rounded-xl border border-dashed transition-colors',
                citiesLoadMoreLoading
                  ? 'border-slate-200 text-muted-foreground cursor-not-allowed dark:border-slate-700'
                  : 'border-primary/40 text-primary hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer'
              )}
            >
              {citiesLoadMoreLoading ? t('common.loading') : t(T_KEYS.FACTORIES.LOAD_MORE_CITIES)}
            </button>
          )}

          {/* Selected count */}
          {(selectedCityIds || []).length > 0 && (
            <p className="text-xs text-primary font-medium">
              {t(T_KEYS.FACTORIES.SELECTED_CITY_COUNT, {
                count: selectedCityIds.length,
              })}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t(T_KEYS.FACTORIES.DESCRIPTION_SECTION)}
          </h3>

          <div className="space-y-2">
            <Label htmlFor="descriptionEn">
              {t(T_KEYS.FACTORIES.DESCRIPTION_EN)}
            </Label>
            <Controller
              name="descriptionEn"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Textarea
                    id="descriptionEn"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.DESCRIPTION_PLACEHOLDER)}
                    className="min-h-[80px] rounded-xl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="descriptionAr">
              {t(T_KEYS.FACTORIES.DESCRIPTION_AR)}
            </Label>
            <Controller
              name="descriptionAr"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Textarea
                    id="descriptionAr"
                    value={value ?? ''}
                    placeholder={t(T_KEYS.FACTORIES.DESCRIPTION_AR_PLACEHOLDER)}
                    className="min-h-[80px] rounded-xl"
                    dir="rtl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>
        </div>
      </form>
    </BaseDrawer>
  )
}
